import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  UserPlus, Loader2, MoreVertical, KeyRound, ShieldOff, Power, Copy, CheckCircle2, Pencil,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { ROLE_LABELS } from "@/lib/constants";
import { useT } from "@/i18n/I18nProvider";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";

export default function Employees() {
  const { user } = useAuth();
  const t = useT();
  const isOwner = user.role === "owner";
  const [users, setUsers] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ username: "", full_name: "", email: "", phone: "", role: "" });
  const [tempPw, setTempPw] = useState(null);
  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ full_name: "", email: "", phone: "", role: "" });
  const [editSaving, setEditSaving] = useState(false);

  const load = () =>
    api.get("/users").then((r) => setUsers(r.data)).finally(() => setLoading(false));

  useEffect(() => {
    load();
    api.get("/meta/roles")
      .then((r) => setRoles(r.data.filter((x) => x.key !== "owner")))
      .catch(() => toast.error(t("employees.rolesLoadError")));
  }, []);

  const create = async (e) => {
    e.preventDefault();
    if (!form.role) return toast.error(t("employees.selectRoleErr"));
    setSaving(true);
    try {
      const { data } = await api.post("/users", form);
      setOpen(false);
      setForm({ username: "", full_name: "", email: "", phone: "", role: "" });
      setTempPw({ username: data.username, code: data.activation_code, title: t("employees.employeeCreated") });
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (u) => {
    setEditForm({
      full_name: u.full_name || "",
      email: u.email || "",
      phone: u.phone || "",
      role: u.role,
    });
    setEditUser(u);
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    if (!editForm.role) return toast.error(t("employees.selectRoleErr"));
    setEditSaving(true);
    try {
      await api.patch(`/users/${editUser.id}`, editForm);
      toast.success(t("employees.employeeUpdated"));
      setEditUser(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setEditSaving(false);
    }
  };

  const act = async (u, action) => {
    try {
      if (action === "deactivate") { await api.post(`/users/${u.id}/deactivate`); toast.success(t("employees.accountDeactivated")); }
      if (action === "activate") { await api.post(`/users/${u.id}/activate`); toast.success(t("employees.accountEnabled")); }
      if (action === "reset-mfa") { await api.post(`/users/${u.id}/reset-mfa`); toast.success(t("employees.mfaReset")); }
      if (action === "apply-main-coordinator") { await api.post(`/users/${u.id}/apply-profile`, { profile: "main_coordinator" }); toast.success(t("employees.mainCoordApplied")); }
      if (action === "reset-password") {
        const { data } = await api.post(`/users/${u.id}/reset-password`);
        setTempPw({ username: u.username, code: data.activation_code, title: t("employees.codeIssued") });
      }
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const copy = (txt) => { navigator.clipboard.writeText(txt); toast.success(t("common.copied")); };

  return (
    <div className="p-6 sm:p-8 max-w-[1400px]">
      <PageHeader title={t("employees.title")} subtitle={t("employees.subtitle")}>
        {isOwner && (
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-employee-button" className="bg-navy hover:bg-navy-soft text-white gap-2">
                <UserPlus className="h-4 w-4" /> {t("employees.addEmployee")}
              </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle className="font-heading">{t("employees.createAccount")}</DialogTitle>
                <DialogDescription>{t("employees.createAccountDesc")}</DialogDescription>
              </DialogHeader>
              <form onSubmit={create} className="space-y-4 mt-2" data-testid="employee-form">
                <div className="space-y-1.5">
                  <Label>{t("employees.username")} *</Label>
                  <Input data-testid="employee-username" value={form.username} onChange={(e) => setForm({ ...form, username: e.target.value })} required />
                </div>
                <div className="space-y-1.5">
                  <Label>{t("employees.fullName")} *</Label>
                  <Input data-testid="employee-fullname" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} required />
                </div>
                <div className="space-y-1.5">
                  <Label>{t("employees.email")}</Label>
                  <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>{t("employees.phone")}</Label>
                  <Input data-testid="employee-phone" value={form.phone} placeholder={t("employees.phonePlaceholder")} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
                </div>
                <div className="space-y-1.5">
                  <Label>{t("employees.role")} *</Label>
                  <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                    <SelectTrigger data-testid="employee-role"><SelectValue placeholder={t("employees.selectRole")} /></SelectTrigger>
                    <SelectContent>
                      {roles.map((r) => (
                        <SelectItem key={r.key} value={r.key}>{t(`roles.${r.key}`, r.label)}{r.privileged ? " · MFA" : ""}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <DialogFooter>
                  <Button type="submit" data-testid="employee-submit" disabled={saving} className="bg-navy hover:bg-navy-soft text-white">
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("employees.createAccountBtn")}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </PageHeader>

      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-16 flex justify-center"><Loader2 className="h-5 w-5 animate-spin text-navy" /></div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>{t("employees.name")}</TableHead>
                <TableHead>{t("employees.username")}</TableHead>
                <TableHead>{t("employees.role")}</TableHead>
                <TableHead>{t("employees.status")}</TableHead>
                <TableHead>{t("employees.mfa")}</TableHead>
                {isOwner && <TableHead className="w-10"></TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} data-testid={`employee-row-${u.username}`}>
                  <TableCell className="font-medium">{u.full_name}</TableCell>
                  <TableCell className="font-mono text-sm text-slate-600">{u.username}</TableCell>
                  <TableCell><Badge variant="secondary" className="font-normal">{t(`roles.${u.role}`, ROLE_LABELS[u.role] || u.role)}</Badge></TableCell>
                  <TableCell>
                    {!u.is_active ? (
                      <span className="inline-flex items-center gap-1.5 text-sm text-red-500"><span className="h-2 w-2 rounded-full bg-red-500" />{t("employees.disabled")}</span>
                    ) : u.activated ? (
                      <span className="inline-flex items-center gap-1.5 text-sm text-emerald-600"><span className="h-2 w-2 rounded-full bg-emerald-500" />{t("employees.active")}</span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 text-sm text-amber-600"><span className="h-2 w-2 rounded-full bg-amber-500" />{t("employees.pendingActivation")}</span>
                    )}
                  </TableCell>
                  <TableCell>
                    {u.mfa_enabled ? <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">{t("employees.enrolled")}</Badge>
                      : u.mfa_required ? <Badge variant="outline" className="text-amber-600 border-amber-300">{t("employees.required")}</Badge>
                      : <span className="text-slate-400 text-sm">—</span>}
                  </TableCell>
                  {isOwner && (
                    <TableCell>
                      {u.role !== "owner" && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon" data-testid={`employee-menu-${u.username}`}><MoreVertical className="h-4 w-4" /></Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => openEdit(u)} data-testid={`edit-employee-${u.username}`}>
                              <Pencil className="h-4 w-4 mr-2" /> {t("employees.edit")}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => act(u, "reset-password")} data-testid={`reset-pw-${u.username}`}>
                              <KeyRound className="h-4 w-4 mr-2" /> {t("employees.issueCode")}
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => act(u, "reset-mfa")}>
                              <ShieldOff className="h-4 w-4 mr-2" /> {t("employees.resetMfa")}
                            </DropdownMenuItem>
                            {u.role === "coordination" && (
                              <DropdownMenuItem onClick={() => act(u, "apply-main-coordinator")} data-testid={`apply-maincoord-${u.username}`}>
                                <KeyRound className="h-4 w-4 mr-2" /> {u.profile === "main_coordinator" ? t("employees.mainCoord") : t("employees.makeMainCoord")}
                              </DropdownMenuItem>
                            )}
                            {u.is_active ? (
                              <DropdownMenuItem onClick={() => act(u, "deactivate")} className="text-red-600" data-testid={`deactivate-${u.username}`}>
                                <Power className="h-4 w-4 mr-2" /> {t("employees.deactivate")}
                              </DropdownMenuItem>
                            ) : (
                              <DropdownMenuItem onClick={() => act(u, "activate")} data-testid={`activate-${u.username}`}>
                                <Power className="h-4 w-4 mr-2" /> {t("employees.activate")}
                              </DropdownMenuItem>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>

      <Dialog open={!!editUser} onOpenChange={(o) => !o && setEditUser(null)}>
        <DialogContent className="sm:max-w-md" data-testid="edit-employee-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{t("employees.editAccount")}</DialogTitle>
            <DialogDescription>{t("employees.editAccountDesc")}</DialogDescription>
          </DialogHeader>
          <form onSubmit={saveEdit} className="space-y-4 mt-2" data-testid="edit-employee-form">
            <div className="space-y-1.5">
              <Label>{t("employees.username")}</Label>
              <Input value={editUser?.username || ""} disabled className="font-mono text-slate-500" data-testid="edit-employee-username" />
            </div>
            <div className="space-y-1.5">
              <Label>{t("employees.fullName")} *</Label>
              <Input data-testid="edit-employee-fullname" value={editForm.full_name} onChange={(e) => setEditForm({ ...editForm, full_name: e.target.value })} required />
            </div>
            <div className="space-y-1.5">
              <Label>{t("employees.email")}</Label>
              <Input type="email" data-testid="edit-employee-email" value={editForm.email} onChange={(e) => setEditForm({ ...editForm, email: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>{t("employees.phone")}</Label>
              <Input data-testid="edit-employee-phone" value={editForm.phone} placeholder={t("employees.phonePlaceholder")} onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>{t("employees.role")} *</Label>
              <Select value={editForm.role} onValueChange={(v) => setEditForm({ ...editForm, role: v })}>
                <SelectTrigger data-testid="edit-employee-role"><SelectValue placeholder={t("employees.selectRole")} /></SelectTrigger>
                <SelectContent>
                  {roles.map((r) => (
                    <SelectItem key={r.key} value={r.key}>{t(`roles.${r.key}`, r.label)}{r.privileged ? " · MFA" : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <DialogFooter>
              <Button type="submit" data-testid="edit-employee-submit" disabled={editSaving} className="bg-navy hover:bg-navy-soft text-white">
                {editSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : t("employees.saveChanges")}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!tempPw} onOpenChange={(o) => !o && setTempPw(null)}>
        <DialogContent className="sm:max-w-sm" data-testid="temp-password-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <CheckCircle2 className="h-5 w-5 text-emerald-500" /> {tempPw?.title}
            </DialogTitle>
            <DialogDescription>{t("employees.shareSecurely")}</DialogDescription>
          </DialogHeader>
          <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 space-y-2">
            <div className="flex justify-between text-sm"><span className="text-slate-500">{t("employees.username")}</span><span className="font-mono">{tempPw?.username}</span></div>
            <div className="flex items-center justify-between">
              <span className="text-slate-500 text-sm">{t("employees.activationCode")}</span>
              <button onClick={() => copy(tempPw?.code)} data-testid="activation-code-value" className="font-mono text-navy font-semibold flex items-center gap-2 nav-link hover:text-gold">
                {tempPw?.code} <Copy className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
